import { Theme } from "@material-ui/core";
import type { Place } from "../../types/warehouse.types";
import { adjustBrightness } from "../../utils/canvasDrawing";
import { ColorSet, PlaceState, RENDER_CONSTANTS, PlaceRendererConfig } from "../core/types";

/**
 * Strategy interface for place styling
 *
 * Pattern: Strategy Pattern
 * Purpose: Allow different styling strategies to be swapped at runtime
 */
export interface IPlaceStyleStrategy {
  getColorSet(place: Place): ColorSet;
  getStroke(place: Place, isSelected: boolean, config: PlaceRendererConfig): {
    color: string;
    width: number;
  };
}

/**
 * Manages place colors and styles with caching
 *
 * Pattern: Flyweight Pattern (for color caching) + Strategy Pattern
 */
export class PlaceStyleManager implements IPlaceStyleStrategy {
  private colorCache = new Map<string, string>();
  private palette: Map<PlaceState, ColorSet>;

  constructor(
    private theme: Theme,
    private config: PlaceRendererConfig
  ) {
    this.palette = this.buildPalette();
  }

  /**
   * Get cached or calculate brightness-adjusted color
   */
  private getCachedColor(baseColor: string, adjustment: number): string {
    const cacheKey = `${baseColor}_${adjustment}`;
    let color = this.colorCache.get(cacheKey);

    if (!color) {
      color = adjustBrightness(baseColor, adjustment);
      this.colorCache.set(cacheKey, color);
    }

    return color;
  }

  /**
   * Build complete color palette for all place states
   */
  private buildPalette(): Map<PlaceState, ColorSet> {
    const { empty, occupied, blocked } = this.theme.palette.storageUi;
    const { FACE_BRIGHTNESS, RIGHT_FACE_BRIGHTNESS, STROKE_BRIGHTNESS } =
      RENDER_CONSTANTS;

    const createSet = (base: string): ColorSet => ({
      top: base,
      face: this.getCachedColor(base, FACE_BRIGHTNESS),
      right: this.getCachedColor(base, RIGHT_FACE_BRIGHTNESS),
      stroke: this.getCachedColor(base, STROKE_BRIGHTNESS),
    });

    const palette = new Map<PlaceState, ColorSet>();
    palette.set(PlaceState.Empty, createSet(empty));
    palette.set(PlaceState.Occupied, createSet(occupied));
    palette.set(PlaceState.Blocked, createSet(blocked));

    return palette;
  }

  /**
   * Determine place state
   */
  private getPlaceState(place: Place): PlaceState {
    if (place.blocked) return PlaceState.Blocked;
    if (place.occupied) return PlaceState.Occupied;
    return PlaceState.Empty;
  }

  /**
   * Get color set for a place (Strategy method)
   */
  getColorSet(place: Place): ColorSet {
    const state = this.getPlaceState(place);
    return this.palette.get(state)!;
  }

  /**
   * Get stroke style for a place (Strategy method)
   */
  getStroke(
    place: Place,
    isSelected: boolean,
    config: PlaceRendererConfig
  ): { color: string; width: number } {
    if (isSelected) {
      return {
        color:
          place.placeSelectionBorderColor ||
          this.theme.palette.storageUi.textPrimary,
        width: place.placeSelectionBorderWidth || config.selectionLineWidth,
      };
    }

    const colorSet = this.getColorSet(place);
    return {
      color: colorSet.stroke,
      width: config.borderLineWidth,
    };
  }

  /**
   * Rebuild palette (call when theme changes)
   */
  updateTheme(theme: Theme): void {
    this.theme = theme;
    this.colorCache.clear();
    this.palette = this.buildPalette();
  }

  /**
   * Clear all caches
   */
  clearCaches(): void {
    this.colorCache.clear();
    this.palette = this.buildPalette();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { colorCacheSize: number; paletteSize: number } {
    return {
      colorCacheSize: this.colorCache.size,
      paletteSize: this.palette.size,
    };
  }
}
